import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  noIndex?: boolean;
}

const defaultSEO = {
  title: 'CopyGenius - Gerador de Copy com IA | Textos que Vendem em 10 Segundos',
  description: 'Gere textos persuasivos para e-commerce, redes sociais e campanhas com IA avançada. Copy profissional em segundos. Teste grátis sem cartão!',
  keywords: 'gerador de copy, copywriting com ia, textos para vendas, copy para ecommerce, copy para redes sociais',
  image: 'https://copygenius.com.br/og-image.jpg',
  url: 'https://copygenius.com.br/',
  type: 'website' as const,
};

export function SEOHead({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  noIndex = false,
}: SEOHeadProps) {
  const seo = {
    title: title ? `${title} | CopyGenius` : defaultSEO.title,
    description: description || defaultSEO.description,
    keywords: keywords || defaultSEO.keywords,
    image: image || defaultSEO.image,
    url: url || defaultSEO.url,
    type,
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{seo.title}</title>
      <meta name="title" content={seo.title} />
      <meta name="description" content={seo.description} />
      <meta name="keywords" content={seo.keywords} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={seo.url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={seo.type} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={seo.url} />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />
    </Helmet>
  );
}

// Pre-configured SEO for common pages
export const PageSEO = {
  login: {
    title: 'Login',
    description: 'Faça login na sua conta CopyGenius e comece a gerar textos persuasivos com IA. Acesse suas gerações e histórico.',
    url: 'https://copygenius.com.br/login',
  },
  register: {
    title: 'Criar Conta Grátis',
    description: 'Crie sua conta gratuita no CopyGenius e comece a gerar textos persuasivos com IA. Sem cartão de crédito, créditos grátis inclusos.',
    url: 'https://copygenius.com.br/register',
  },
  forgot: {
    title: 'Recuperar Senha',
    description: 'Recupere o acesso à sua conta CopyGenius. Enviaremos um link para redefinir sua senha.',
    url: 'https://copygenius.com.br/forgot',
    noIndex: true,
  },
  dashboard: {
    title: 'Dashboard',
    description: 'Seu painel de controle CopyGenius. Gere copy, veja histórico e gerencie sua conta.',
    noIndex: true,
  },
  generate: {
    title: 'Gerar Copy',
    description: 'Gere textos persuasivos para e-commerce com IA. Descrições de produtos, títulos e bullets que vendem.',
    noIndex: true,
  },
  socialPosts: {
    title: 'Posts para Redes Sociais',
    description: 'Crie posts incríveis para Instagram, Facebook e outras redes sociais com IA. Legendas, imagens e hashtags.',
    noIndex: true,
  },
  history: {
    title: 'Histórico',
    description: 'Acesse todas as suas gerações anteriores. Revise, reutilize e gerencie seus textos.',
    noIndex: true,
  },
  account: {
    title: 'Minha Conta',
    description: 'Gerencie sua conta CopyGenius. Configurações, planos e dados pessoais.',
    noIndex: true,
  },
};
